import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TestParametersForm = ({ batchData, onSubmit, onSave }) => {
  const [testParameters, setTestParameters] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [equipmentUsed, setEquipmentUsed] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const availableTests = {
    tablet: [
      { id: 'weight_variation', name: 'Weight Variation', unit: 'mg', range: '±5%', required: true },
      { id: 'hardness', name: 'Hardness Test', unit: 'N', range: '40-120', required: true },
      { id: 'friability', name: 'Friability Test', unit: '%', range: '<1.0', required: true },
      { id: 'disintegration', name: 'Disintegration Time', unit: 'min', range: '<15', required: true },
      { id: 'dissolution', name: 'Dissolution Test', unit: '%', range: '>80 in 30min', required: true },
      { id: 'assay', name: 'Assay (Potency)', unit: '%', range: '95-105', required: true },
      { id: 'content_uniformity', name: 'Content Uniformity', unit: '%', range: '85-115', required: false }
    ],
    capsule: [
      { id: 'weight_variation', name: 'Weight Variation', unit: 'mg', range: '±10%', required: true },
      { id: 'disintegration', name: 'Disintegration Time', unit: 'min', range: '<30', required: true },
      { id: 'dissolution', name: 'Dissolution Test', unit: '%', range: '>75 in 45min', required: true },
      { id: 'assay', name: 'Assay (Potency)', unit: '%', range: '95-105', required: true },
      { id: 'moisture_content', name: 'Moisture Content', unit: '%', range: '<5.0', required: false }
    ],
    liquid: [
      { id: 'ph', name: 'pH Test', unit: 'pH', range: '6.0-8.0', required: true },
      { id: 'assay', name: 'Assay (Potency)', unit: '%', range: '95-105', required: true },
      { id: 'viscosity', name: 'Viscosity', unit: 'cP', range: 'As specified', required: false },
      { id: 'density', name: 'Density', unit: 'g/mL', range: 'As specified', required: false },
      { id: 'microbial_limits', name: 'Microbial Limits', unit: 'CFU/mL', range: '<100', required: true }
    ],
    injection: [
      { id: 'sterility', name: 'Sterility Test', unit: 'Pass/Fail', range: 'Pass', required: true },
      { id: 'endotoxin', name: 'Endotoxin Test', unit: 'EU/mL', range: '<0.5', required: true },
      { id: 'assay', name: 'Assay (Potency)', unit: '%', range: '95-105', required: true },
      { id: 'ph', name: 'pH Test', unit: 'pH', range: '6.5-7.5', required: true },
      { id: 'particulate_matter', name: 'Particulate Matter', unit: 'particles/mL', range: 'As per USP', required: true }
    ]
  };

  const equipmentOptions = [
    { value: 'hplc_001', label: 'HPLC System #001 - Agilent 1260' },
    { value: 'hplc_002', label: 'HPLC System #002 - Waters Alliance' },
    { value: 'uv_spec_001', label: 'UV Spectrophotometer #001 - Shimadzu' },
    { value: 'dissolution_001', label: 'Dissolution Tester #001 - Erweka' },
    { value: 'hardness_001', label: 'Hardness Tester #001 - Pharma Test' },
    { value: 'friability_001', label: 'Friability Tester #001 - Electrolab' },
    { value: 'balance_001', label: 'Analytical Balance #001 - Mettler Toledo' },
    { value: 'ph_meter_001', label: 'pH Meter #001 - Hanna Instruments' }
  ];

  useEffect(() => {
    if (batchData?.productType) {
      const tests = availableTests?.[batchData?.productType] || [];
      setTestParameters(tests);
      
      // Auto-select required tests
      const requiredTests = tests?.filter(test => test?.required)?.map(test => test?.id);
      setSelectedTests(requiredTests);
      
      // Initialize test results for required tests
      const initialResults = {};
      const initialEquipment = {};
      requiredTests?.forEach(testId => {
        initialResults[testId] = '';
        initialEquipment[testId] = '';
      });
      setTestResults(initialResults);
      setEquipmentUsed(initialEquipment);
    }
  }, [batchData]);

  const handleTestSelection = (testId, checked) => {
    if (checked) {
      setSelectedTests(prev => [...prev, testId]);
      setTestResults(prev => ({ ...prev, [testId]: '' }));
      setEquipmentUsed(prev => ({ ...prev, [testId]: '' }));
    } else {
      // Don't allow deselecting required tests
      const test = testParameters?.find(t => t?.id === testId);
      if (test?.required) return;
      
      setSelectedTests(prev => prev?.filter(id => id !== testId));
      setTestResults(prev => {
        const newResults = { ...prev };
        delete newResults?.[testId];
        return newResults;
      });
      setEquipmentUsed(prev => {
        const newEquipment = { ...prev };
        delete newEquipment?.[testId];
        return newEquipment;
      });
    }
  };

  const handleResultChange = (testId, value) => {
    setTestResults(prev => ({
      ...prev,
      [testId]: value
    }));

    // Clear error when user starts typing
    if (errors?.[testId]) {
      setErrors(prev => ({
        ...prev,
        [testId]: ''
      }));
    }
  };

  const handleEquipmentChange = (testId, equipmentId) => {
    setEquipmentUsed(prev => ({
      ...prev,
      [testId]: equipmentId
    }));
  };

  const validateResults = () => {
    const newErrors = {};

    selectedTests?.forEach(testId => {
      if (!testResults?.[testId] || testResults?.[testId]?.toString()?.trim() === '') {
        newErrors[testId] = 'Test result is required';
      }
      
      if (!equipmentUsed?.[testId]) {
        newErrors[`${testId}_equipment`] = 'Equipment selection is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateResults()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const testData = {
        selectedTests,
        testResults,
        equipmentUsed,
        timestamp: new Date()?.toISOString(),
        technician: 'Current User' // This would come from auth context
      };
      
      await onSubmit(testData);
    } catch (error) {
      console.error('Test submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getResultStatus = (testId, result) => {
    const test = testParameters?.find(t => t?.id === testId);
    if (!test || !result) return 'pending';

    // Simple validation logic - in real app this would be more sophisticated
    const numericResult = parseFloat(result);
    if (isNaN(numericResult)) {
      return result?.toLowerCase() === 'pass' ? 'pass' : 'fail';
    }

    // This is simplified - real validation would parse range properly
    return 'pass'; // Placeholder
  };

  if (!batchData?.productType) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="FlaskConical" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            Please complete batch information first to configure test parameters
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FlaskConical" size={16} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Test Parameters</h3>
            <p className="text-sm text-muted-foreground">
              Configure and execute quality control tests for {batchData?.productType} products
            </p>
          </div>
        </div>
      </div>
      {/* Test Selection */}
      <div className="space-y-6">
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">Available Tests</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {testParameters?.map((test) => (
              <div key={test?.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                <Checkbox
                  checked={selectedTests?.includes(test?.id)}
                  onChange={(e) => handleTestSelection(test?.id, e?.target?.checked)}
                  disabled={test?.required}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">{test?.name}</span>
                    {test?.required && (
                      <span className="text-xs bg-error/10 text-error px-2 py-0.5 rounded-full">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Range: {test?.range} {test?.unit}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Test Results Entry */}
        {selectedTests?.length > 0 && (
          <div className="border-t border-border pt-6">
            <h4 className="text-md font-medium text-foreground mb-4">Test Results Entry</h4>
            <div className="space-y-4">
              {selectedTests?.map((testId) => {
                const test = testParameters?.find(t => t?.id === testId);
                const result = testResults?.[testId];
                const status = getResultStatus(testId, result);
                
                return (
                  <div key={testId} className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 border border-border rounded-lg">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <h5 className="font-medium text-foreground">{test?.name}</h5>
                        {status !== 'pending' && (
                          <div className={`w-2 h-2 rounded-full ${
                            status === 'pass' ? 'bg-success' : 
                            status === 'fail' ? 'bg-error' : 'bg-warning'
                          }`} />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Expected: {test?.range} {test?.unit}
                      </p>
                    </div>
                    <div>
                      <Input
                        label="Result Value"
                        type={test?.unit === 'Pass/Fail' ? 'text' : 'number'}
                        placeholder={test?.unit === 'Pass/Fail' ? 'Pass or Fail' : `Enter value in ${test?.unit}`}
                        value={result}
                        onChange={(e) => handleResultChange(testId, e?.target?.value)}
                        error={errors?.[testId]}
                        required
                      />
                    </div>
                    <div>
                      <Select
                        label="Equipment Used"
                        placeholder="Select equipment"
                        options={equipmentOptions}
                        value={equipmentUsed?.[testId] || ''}
                        onChange={(value) => handleEquipmentChange(testId, value)}
                        error={errors?.[`${testId}_equipment`]}
                        required
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Form Actions */}
        {selectedTests?.length > 0 && (
          <div className="flex items-center justify-between pt-6 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {selectedTests?.length} test{selectedTests?.length !== 1 ? 's' : ''} configured
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => onSave && onSave({ selectedTests, testResults, equipmentUsed })}
                disabled={isSubmitting}
              >
                Save Progress
              </Button>
              <Button
                variant="default"
                onClick={handleSubmit}
                loading={isSubmitting}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Submit Test Results
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestParametersForm;